import React, { createContext, useRef, useCallback, ReactNode } from 'react';
import { Player, AudioRealtimeData } from '@/utils/pcmPlayer';

export interface AudioPlayerContextType {
    playerRef: React.MutableRefObject<Player | null>;
    realtimeData: AudioRealtimeData;
    initPlayer: (sampleRate?: number) => void;
    playAudio: (pcmData: Int16Array) => void;
    playFromUrl: (url: string) => Promise<void>;
    playFromBlob: (blob: Blob) => Promise<void>;
    playFromBase64: (base64: string, mimeType?: string) => Promise<void>;
    stopAudio: () => void;
    pauseAudio: () => void;
    resumeAudio: () => Promise<void>;
    isPlaying: () => boolean;
    isPlayerInitialized: () => boolean;
}

export const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

interface AudioPlayerProviderProps {
    children: ReactNode;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
    const playerRef = useRef<Player | null>(null);

    const initPlayer = useCallback((sampleRate: number = 24000) => {
        if (!playerRef.current) {
            playerRef.current = new Player();
        }
        playerRef.current.init(sampleRate);
    }, []);

    const playAudio = useCallback((pcmData: Int16Array) => {
        if (playerRef.current) {
            playerRef.current.play(pcmData);
        } else {
            console.warn('AudioPlayer: Player not initialized. Call initPlayer() first.');
        }
    }, []);

    const playFromUrl = useCallback(async (url: string) => {
        if (playerRef.current) {
            await playerRef.current.playFromUrl(url);
        } else {
            console.warn('AudioPlayer: Player not initialized. Call initPlayer() first.');
        }
    }, []);

    const playFromBlob = useCallback(async (blob: Blob) => {
        if (playerRef.current) {
            await playerRef.current.playFromBlob(blob);
        } else {
            console.warn('AudioPlayer: Player not initialized. Call initPlayer() first.');
        }
    }, []);

    const playFromBase64 = useCallback(async (base64: string, mimeType: string = 'audio/mp3') => {
        if (playerRef.current) {
            await playerRef.current.playFromBase64(base64, mimeType);
        } else {
            console.warn('AudioPlayer: Player not initialized. Call initPlayer() first.');
        }
    }, []);

    const stopAudio = useCallback(() => {
        if (playerRef.current) {
            playerRef.current.stop();
        }
    }, []);

    const pauseAudio = useCallback(() => {
        if (playerRef.current) {
            playerRef.current.pause();
        }
    }, []);

    const resumeAudio = useCallback(async () => {
        if (playerRef.current) {
            await playerRef.current.resume();
        }
    }, []);

    const isPlaying = useCallback(() => {
        return playerRef.current?.isPlaying() ?? false;
    }, []);

    const isPlayerInitialized = useCallback(() => {
        return playerRef.current?.isInitialized() ?? false;
    }, []);

    // Expose realtimeData as direct reference (getter)
    const getRealtimeDataRef = () => playerRef.current?.realtimeData ?? null;

    const value: AudioPlayerContextType = {
        playerRef,
        get realtimeData() {
            return getRealtimeDataRef();
        },
        initPlayer,
        playAudio,
        playFromUrl,
        playFromBlob,
        playFromBase64,
        stopAudio,
        pauseAudio,
        resumeAudio,
        isPlaying,
        isPlayerInitialized,
    };

    return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
};
